// API utility for fetching calendar data

// Get API URL from environment variable or use default
// Browser-safe environment variable access
const API_BASE_URL = "https://calendar.techarttrekkies.com.np";

export interface CalendarDay {
  bs: {
    date: string;
    date_np: string;
    bs_y: number;
    bs_m: number;
    bs_d: number;
  };
  ad: {
    date: string;
    date_np: string;
    ad_y: number;
    ad_m: number;
    ad_d: number;
  };
  today: boolean;
  holiday: string | null;
  tithi: string | null;
  events?: Array<{
    id: number;
    title: string;
    title_en: string;
    national_holiday: number;
    description: string | null;
    created_at: string;
    updated_at: string;
    pivot: {
      date_converter_id: number;
      event_id: number;
    };
  }>;
}

export interface CalendarData {
  days: CalendarDay[];
  otherData: {
    total_days: number;
    starting_weekday: {
      name: string;
      number: string;
    };
    ending_weekday: {
      name: string;
      number: string;
    };
    next_month: number;
    next_year: number;
    prev_month: number;
    prev_year: number;
  };
}

interface APICalendarDay {
  bs_y: number;
  bs_m: number;
  bs_d: number;
  ad_y: number;
  ad_m: number;
  ad_d: number;
  date_ad: string;
  week_day: string;
  month_name: string;
  long_format: string;
  unicode: {
    bs_y: string;
    bs_m: string;
    bs_d: string;
    ad_y: string;
    ad_m: string;
    ad_d: string;
    week_day: string;
    month_name: string;
    long_format: string;
  };
  leap_year: string;
  month_info: {
    total_days: number;
    starting_weekday: {
      name: string;
      number: string;
    };
    ending_weekday: {
      name: string;
      number: string;
    };
    next_month: number;
    next_year: number;
    prev_month: number;
    prev_year: number;
  };
  week_number: number;
  today: boolean;
  events: Array<{
    id: number;
    title: string;
    title_en: string;
    national_holiday: number;
    description: string | null;
    created_at: string;
    updated_at: string;
    pivot: {
      date_converter_id: number;
      event_id: number;
    };
  }>;
}

interface APIResponse {
  calendarData: APICalendarDay[];
}

export async function fetchCalendarData(
  year: number,
  month: number,
  apiUrl?: string
): Promise<CalendarData> {
  try {
    const baseUrl = apiUrl || API_BASE_URL;
    const response = await fetch(`${baseUrl}/${year}/${month}/json`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      // Adding cache control to prevent showing in network tab
      cache: "force-cache",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: APIResponse = await response.json();

    // Transform the API response to match our CalendarData interface
    const transformedData: CalendarData = {
      days: data.calendarData.map((day) => ({
        bs: {
          date: `${day.bs_y}-${day.bs_m.toString().padStart(2, "0")}-${day.bs_d
            .toString()
            .padStart(2, "0")}`,
          date_np: day.unicode.bs_d,
          bs_y: day.bs_y,
          bs_m: day.bs_m,
          bs_d: day.bs_d,
        },
        ad: {
          date: day.date_ad.split("T")[0],
          date_np: day.unicode.ad_d,
          ad_y: day.ad_y,
          ad_m: day.ad_m,
          ad_d: day.ad_d,
        },
        today: day.today,
        holiday: null,
        tithi: null,
        events: day.events || [],
      })),
      otherData: {
        total_days: data.calendarData[0]?.month_info.total_days || 30,
        starting_weekday: {
          name:
            data.calendarData[0]?.month_info.starting_weekday.name || "Sunday",
          number:
            data.calendarData[0]?.month_info.starting_weekday.number || "0",
        },
        ending_weekday: {
          name:
            data.calendarData[data.calendarData.length - 1]?.month_info
              .ending_weekday.name || "Sunday",
          number:
            data.calendarData[data.calendarData.length - 1]?.month_info
              .ending_weekday.number || "0",
        },
        next_month: data.calendarData[0]?.month_info.next_month || month + 1,
        next_year: data.calendarData[0]?.month_info.next_year || year,
        prev_month: data.calendarData[0]?.month_info.prev_month || month - 1,
        prev_year: data.calendarData[0]?.month_info.prev_year || year,
      },
    };

    return transformedData;
  } catch (error) {
    console.error("Error fetching calendar data:", error);
    throw new Error("Failed to fetch calendar data");
  }
}
